import { Container } from '@mui/material'
import { Box } from '@mui/system'
import { useContext, useEffect, useMemo, useState } from 'react'
import { format, subDays, parse } from 'date-fns'

import { AlertComponent } from 'src/components/Alert/AlertComponent'
import { ModalPassword } from 'src/components/Modal/ModalPassword'
import DashboardViewProvider from 'src/context/DashboardViewContext'
import { useGlobalSelectedCompany } from 'src/context/GlobalSelectedCompanyContext'
import { ModalChangePasswordContext } from 'src/context/ModalChangePasswordContext'
import {
  getDashboardAccessPointRanking,
  getDashboardActivity,
  getDashboardAverageReconnections,
  getDashboardGroupsRanking,
  getDashboardNewUsers,
  getDashboardReturningUsers,
  getDashboardTotalConnections,
} from 'src/service/dashboardService'
import { DashboardMetricsData } from 'src/models/dashboardData'

import { AccessRanking } from './Cards/AccessRanking'
import { Connections } from './Cards/Connections'
import { PerGroupConnections } from './Cards/PerGroupConnections'
import { ReconnectionsAverage } from './Cards/ReconnectionsAverage'
import { UsePerPeriod } from './Cards/UsePerPeriod'
import { Users } from './Cards/Users'
import { TotalAccessPerMonth } from './Cards/TotalAccessPerMonth'

import { DashboardHeader } from './DashboardHeader'
import { DashboardDataService } from './DashboardDataService'

// Quantidade default de Access Points retornados pela nova rota granular.
// O componente atual exibe até 10 entradas (slice no AccessRanking).
const ACCESS_POINT_RANKING_LIMIT = 10

// Chaves de loading — uma por rota granular do Dashboard. Mantemos um
// boolean por métrica para que cada card mostre seu próprio overlay de
// loading independentemente dos outros (renderização progressiva).
type MetricKey =
  | 'totalConnections'
  | 'averageReconnections'
  | 'activity'
  | 'newUsers'
  | 'returningUsers'
  | 'accessPointRanking'
  | 'groupsRanking'

type LoadingState = Record<MetricKey, boolean>

const buildLoadingState = (value: boolean): LoadingState => ({
  totalConnections: value,
  averageReconnections: value,
  activity: value,
  newUsers: value,
  returningUsers: value,
  accessPointRanking: value,
  groupsRanking: value,
})

export const Dashboard = () => {
  const { open, handleCloseModal } = useContext(ModalChangePasswordContext)
  const { selectedCompanyId, isGlobalAdminSelected } = useGlobalSelectedCompany()

  const dashboardService = useMemo(() => new DashboardDataService(), [])

  const today = format(new Date(), 'MM/dd/yyyy')
  const yesterday = format(subDays(new Date(), 1), 'MM/dd/yyyy')

  const [period, setPeriod] = useState('1d')
  const [data, setData] = useState<DashboardMetricsData>({})
  const [loading, setLoading] = useState<LoadingState>(() =>
    buildLoadingState(false)
  )
  const [initialDate, setInitialDate] = useState(yesterday)
  const [finalDate, setFinalDate] = useState(today)

  const formatForApi = (date: string) => {
    const parsed = parse(date, 'MM/dd/yyyy', new Date())
    return format(parsed, 'yyyy/MM/dd')
  }

  /**
   * Aplica o resultado de uma das 7 rotas granulares ao estado agregado.
   * Cada rota devolve apenas a chave da sua métrica (ou `false` em caso
   * de erro). Quando falha, mantemos o estado anterior para não derrubar
   * o widget — o tratamento de erro é por widget, como descrito no
   * NOVO_DASHBOARD.md (seção 7).
   */
  const mergeMetric = (result: any) => {
    if (!result) return
    setData((prev) => ({ ...prev, ...result }))
  }

  /**
   * Helper que envolve cada chamada de rota granular acionando/desligando
   * o flag de loading correspondente. O overlay de loading do card é
   * apresentado enquanto a promise não resolve.
   */
  const runMetric = async <T,>(
    key: MetricKey,
    fn: () => Promise<T>
  ): Promise<void> => {
    try {
      const result = await fn()
      mergeMetric(result)
    } finally {
      setLoading((prev) => ({ ...prev, [key]: false }))
    }
  }

  const fetchData = async (
    start = initialDate,
    end = finalDate,
    periodValue = period
  ) => {
    const paramsCompanyId = isGlobalAdminSelected()
      ? {}
      : { companyId: selectedCompanyId }

    const commonParams = {
      time: periodValue,
      dateStart: formatForApi(start),
      dateEnd: formatForApi(end),
      ...paramsCompanyId,
    }

    const commonOptions = { params: commonParams }

    // Reinicia estado de dados e marca todas as métricas como loading
    // para que cada card exiba seu overlay enquanto sua rota não retorna.
    setData({})
    setLoading(buildLoadingState(true))

    // Dispara as 7 chamadas em paralelo. Cada `.then` aplica o setState
    // do widget correspondente assim que chega (renderização progressiva)
    // e libera o flag de loading da métrica.
    await Promise.all([
      runMetric('totalConnections', () =>
        getDashboardTotalConnections(commonOptions)
      ),
      runMetric('averageReconnections', () =>
        getDashboardAverageReconnections(commonOptions)
      ),
      runMetric('activity', () => getDashboardActivity(commonOptions)),
      runMetric('newUsers', () => getDashboardNewUsers(commonOptions)),
      runMetric('returningUsers', () =>
        getDashboardReturningUsers(commonOptions)
      ),
      runMetric('accessPointRanking', () =>
        getDashboardAccessPointRanking({
          params: { ...commonParams, limit: ACCESS_POINT_RANKING_LIMIT },
        })
      ),
      runMetric('groupsRanking', () =>
        getDashboardGroupsRanking(commonOptions)
      ),
    ])
  }

  const onChangeDateInitial = (date: string) => {
    setInitialDate(date)
    fetchData(date, finalDate, period)
  }

  const onChangeDateFinal = (date: string) => {
    setFinalDate(date)
    fetchData(initialDate, date, period)
  }

  // Só dispara no mount e quando a empresa selecionada muda.
  // Mudanças de período/data são tratadas chamando `fetchData` diretamente
  // no handler do DashboardHeader (evita race com o batch do setState).
  useEffect(() => {
    fetchData()
  }, [selectedCompanyId])

  const sectionStyle = {
    maxWidth: '950px',
    width: '100%',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '15px',
    mt: '16px',
  }

  return (
    <DashboardViewProvider>
      <Container sx={{ mt: '64px' }}>
        <Box
          sx={{
            width: '100%',
            minHeight: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'baseline',
            px: '28px',
            pb: '50px',
            position: 'relative',
          }}
        >
          <ModalPassword open={open} handleClose={handleCloseModal} />

          <Box sx={{ width: '100%', maxWidth: '950px', mt: 2 }}>
            <AlertComponent />
          </Box>

          <DashboardHeader
            onPeriodChange={(periodValue, _periodName, initial, final) => {
              setPeriod(periodValue)
              setInitialDate(initial)
              setFinalDate(final)
              // Chama imediatamente com os valores recebidos para evitar
              // depender do timing do useEffect + closure dos setStates
              // (que ficariam stale para período/datas no mesmo render).
              fetchData(initial, final, periodValue)
            }}
            onChangeDateInitial={onChangeDateInitial}
            onChangeDateFinal={onChangeDateFinal}
          />

          <Box sx={sectionStyle}>
            <TotalAccessPerMonth />
          </Box>

          <Box sx={sectionStyle}>
            <Connections
              value={dashboardService.getConnections(data)}
              loading={loading.totalConnections}
            />
            <ReconnectionsAverage
              value={dashboardService.getAverageReconnections(data)}
              loading={loading.averageReconnections}
            />
            <PerGroupConnections
              labels={dashboardService.getConnectionsPerGroupLabels(data)}
              data={dashboardService.getConnectiosPerGroupValues(data)}
              total={dashboardService.getConnectiosPerGroupTotalValue(data)}
              loading={loading.groupsRanking}
            />
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                gap: '16px',
                flexWrap: 'wrap',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <UsePerPeriod
                  labels={dashboardService.getPeriodUseLabels(data)}
                  data={dashboardService.getPeriodUseValues(data)}
                  loading={loading.activity}
                />
                <Users
                  newUsersValue={dashboardService.getNewUsers(data)}
                  returningUsersValue={dashboardService.getReturningUsers(data)}
                  loading={loading.newUsers || loading.returningUsers}
                />
              </Box>
              <AccessRanking
                data={dashboardService.getAccessRankingData(data)}
                loading={loading.accessPointRanking}
              />
            </Box>
          </Box>
        </Box>
      </Container>
    </DashboardViewProvider>
  )
}
